import React, { useContext, useState } from 'react';
import { ApiContext } from '@api/api';
import { CompanyListOutDTO, MessageResponse, PageResponseCompanyListOutDTO } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import { Clickable, Heading, RowWithSpaceBetween } from '@components/styles';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { stringYMDFromStringInTz } from '@utils/dateWithTz';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { FilterPage, useFilter } from '@hooks/useFilter/Filter.tsx';
import { proxiedPropertiesOf } from '@hooks/useForm/useForm.tsx';
import { Link } from 'react-router-dom';
import EmailPreferencesV2Dialog from '@components/pages/admin/EmailPreferences/EmailPreferencesV2Dialog.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import ConfirmDeleteCompanyDialog from '@components/pages/admin/companies/ConfirmDeleteCompanyDialog.tsx';

interface ComponentProps {
}

interface Filter {
  name?: string;
  ico?: string;
}

const AllCompanies: React.FC<ComponentProps> = () => {
    const { adminListCompanies, adminDeleteCompany } = useContext(ApiContext);
    const [companies, setCompanies] = useState<PageResponseCompanyListOutDTO>();
    const [showNotificationV2SBForCompanyId, setShowNotificationV2SBForCompanyId] = useState(undefined);
    const [companyToDelete, setCompanyToDelete] = useState<CompanyListOutDTO | undefined>(undefined);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | undefined>(undefined);
    const nav = useNavigation();
    const { tr } = useTranslationLgs();
    const { showToastMessage } = useContext(ToastContext);

    const { Filter, applyFilterValue, filter, applyPaging, page, onPage } = useFilter<Filter>({
      onLoad: (page: FilterPage, filter: Filter) => {
        load(page.pageSize, page.pageNumber);
      },
      filterName: 'all_companies',
    });

    const load = (pageSize: number, pageNumber: number) => {
      adminListCompanies({ pageNumber, pageSize }, filter.name, filter.ico, data => {
        setCompanies(data);
      });
    };

    const names = proxiedPropertiesOf<Filter>();

    const deleteCompany = (companyId: string) => {
      setIsDeleting(true);
      setDeleteError(undefined);
      adminDeleteCompany(
        companyId,
        () => {
          setIsDeleting(false);
          setCompanyToDelete(undefined);
          showToastMessage(tr('generic.success', 'Success'), tr('adminCompanyDetail.deleted', 'Company deleted'), 'success');
          load(companies.size, companies.page);
        },
        undefined,
        {
          onValidationFailed: (messages: MessageResponse[]) => {
            setIsDeleting(false);
            setDeleteError(messages?.map(m => m.message).join(' ') || tr('adminCompanyDetail.deleteFailed', 'Failed to delete company'));
          },
          onError: (error: Error) => {
            setIsDeleting(false);
            setDeleteError(error?.message || tr('adminCompanyDetail.deleteFailed', 'Failed to delete company'));
          },
        },
      );
    };

    return (
      <>
        <RowWithSpaceBetween>
          <Heading>Spolecnosti</Heading>
          <Clickable>
            <Link to={'/admin/import-companies'}>Import </Link>
          </Clickable>
        </RowWithSpaceBetween>
        <div className={'flex flex-row gap-4 mt-2'}>
          <InputText placeholder={'Company name'} value={filter?.name}
                     onChange={e => applyFilterValue(names.name, e.target.value)} />
          <InputText placeholder={'Company CRN'} value={filter?.ico}
                     onChange={e => applyFilterValue(names.ico, e.target.value)} />
        </div>
        {companies?.data && (
          <>
            {/*// @ts-ignore*/}
            <DataTable
              value={companies.data}
              rows={companies.size}
              totalRecords={companies.total}
              onPage={onPage}
              first={(companies.page - 1) * companies.size}
              paginator={true}
              lazy={true}
            >
              <Column header={'ID'} body={(row: CompanyListOutDTO) => <Link
                to={`/admin/company/${row.companyId}`}>{row.companyId}</Link>} />
              <Column header={'Name'} body={(row: CompanyListOutDTO) => <div>{row.name}</div>} />
              <Column header={'CRN'} body={(row: CompanyListOutDTO) => <div>{row.crn}</div>} />
              <Column header={'CreatedAt'}
                      body={(row: CompanyListOutDTO) => <div>{stringYMDFromStringInTz(row.createdAt, 'Europe/Prague')}</div>} />
              <Column
                header={'Action'}
                body={(row: CompanyListOutDTO) => (
                  <>
                    <Button
                      label={'Email Prefs'}
                      onClick={e => {
                        setShowNotificationV2SBForCompanyId(row.companyId);
                      }}
                    ></Button>
                    <Button
                      label={'Nastavit timesloty'}
                      onClick={e => {
                        nav.navigate(nav.urlFunctions.createAdminTimeslotSettings(row.companyId));
                      }}
                    ></Button>
                    <Button
                      label={'Nastavit SN'}
                      onClick={e => {
                        nav.navigate(nav.urlFunctions.createAdminSupplierNotificationSettings(row.companyId));
                      }}
                    ></Button>
                    <Button
                      label={'Nastavit expedici'}
                      onClick={e => {
                        nav.navigate(nav.urlFunctions.createAdminExpeditionSettings(row.companyId));
                      }}
                    ></Button>
                    {/* The AI_DISPATCHER feature gates this too, but CompanyListOutDTO does not
                        carry the company's features, so the button is always shown and the
                        backend refuses the call for a company without it. */}
                    <Button
                      label={'AI dispatcher'}
                      onClick={() => {
                        nav.navigate(nav.urlFunctions.createAdminAiDispatcherSettings(row.companyId));
                      }}
                    ></Button>
                    <Button
                      label={'AI activity'}
                      onClick={() => {
                        nav.navigate(nav.urlFunctions.createAdminAiDispatcherActivity(row.companyId));
                      }}
                    ></Button>
                    <Button
                      label={tr('AllCompanies.delete', 'Smazat')}
                      icon="pi pi-trash"
                      className="p-button-danger"
                      onClick={() => {
                        setDeleteError(undefined);
                        setCompanyToDelete(row);
                      }}
                    ></Button>
                  </>
                )}
              ></Column>
            </DataTable>
          </>
        )}
        <EmailPreferencesV2Dialog
          visible={showNotificationV2SBForCompanyId !== undefined}
          onHide={() => setShowNotificationV2SBForCompanyId(undefined)}
          companyId={showNotificationV2SBForCompanyId}
        />
        <ConfirmDeleteCompanyDialog
          visible={companyToDelete !== undefined}
          onHide={() => setCompanyToDelete(undefined)}
          onConfirm={() => companyToDelete && deleteCompany(companyToDelete.companyId)}
          companyName={companyToDelete?.name ?? ''}
          deleting={isDeleting}
          serverError={deleteError}
        />
      </>
    );
  }
;

export default AllCompanies;
;
;
